import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, AlertTriangle, Loader2, FileText, Sparkles, LifeBuoy } from 'lucide-react';
import { useTerms, useAcceptTerms, renderTermsHtml } from '../../hooks/useTerms';

/**
 * Úvodní okno po prvním přihlášení majitele.
 *
 * Kdo co vidí:
 *  • majitel (ADMIN) bez souhlasu → blokující modal, bez odsouhlasení se dál nedostane
 *  • ostatní role                 → informační pruh, že majitel ještě neodsouhlasil
 *  • účet podpory (superadmin)    → jen pruh, souhlas za klienta udělit nemůže
 */
export default function TermsGate() {
  const { data, isLoading, isError, refetch } = useTerms();
  const accept = useAcceptTerms();

  const [checked,      setChecked]      = useState(false);
  const [scrolledEnd,  setScrolledEnd]  = useState(false);
  // API vrátilo 451 → systém je zamčený, i kdyby se /terms zrovna nenačetlo.
  const [blocked,      setBlocked]      = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onBlocked = () => { setBlocked(true); refetch(); };
    window.addEventListener('terms-required', onBlocked);
    return () => window.removeEventListener('terms-required', onBlocked);
  }, [refetch]);

  const required = !!data?.required;

  // Krátký text se nemusí rolovat – pak zaškrtávátko odemkni rovnou.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !required) return;
    if (el.scrollHeight - el.clientHeight < 40) setScrolledEnd(true);
  }, [required, data?.body]);

  // Systém je zamčený, ale podmínky se nepodařilo načíst – bez téhle pojistky
  // by uživatel viděl jen prázdné stránky a neměl jak souhlas udělit.
  if (blocked && (isError || !data?.available)) {
    return (
      <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center space-y-3">
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto"/>
          <h2 className="text-lg font-semibold text-gray-900">Je potřeba odsouhlasit obchodní podmínky</h2>
          <p className="text-sm text-gray-600">
            Podmínky se teď nepodařilo načíst. Zkuste to prosím znovu — pokud potíže přetrvávají,
            ozvěte se podpoře.
          </p>
          <button onClick={() => refetch()}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
            Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !data?.available) return null;

  // ── Pruh pro ostatní role a pro podporu ──────────────────────────────────
  if (!required) {
    if (!data.pending) return null;
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-900">
        <AlertTriangle className="h-4 w-4 shrink-0"/>
        <span>
          {data.support
            ? 'Klient zatím neodsouhlasil obchodní podmínky. Souhlas může udělit pouze majitel účtu.'
            : 'Majitel účtu zatím neodsouhlasil obchodní podmínky systému.'}
        </span>
      </div>
    );
  }

  // ── Blokující úvodní okno majitele ───────────────────────────────────────
  const onScroll = () => {
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledEnd(true);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        {/* Hlavička */}
        <div className="px-7 pt-7 pb-5 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-2 text-blue-100 text-xs font-semibold uppercase tracking-wide mb-2">
            <Sparkles className="h-3.5 w-3.5"/> Vítejte v systému
          </div>
          <h2 className="text-2xl font-bold">Děkujeme, že používáte CarFlow4U</h2>
          <p className="text-blue-50 text-sm mt-2 leading-relaxed whitespace-pre-line">
            {data.intro?.trim()
              || 'Systém máte připravený k používání. Než začnete, projděte si prosím obchodní podmínky a potvrďte je — stačí jednou, příště už se toto okno neobjeví.'}
          </p>
        </div>

        {/* Rychlé info */}
        <div className="px-7 py-4 border-b border-gray-100 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="flex gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-600 shrink-0 mt-0.5"/>
            <div>
              <div className="font-medium text-gray-900">Změňte si heslo</div>
              <div className="text-gray-500 text-xs">Nastavení → Uživatelé → vaše jméno</div>
            </div>
          </div>
          <div className="flex gap-2">
            <FileText className="h-4 w-4 text-blue-600 shrink-0 mt-0.5"/>
            <div>
              <div className="font-medium text-gray-900">Podmínky kdykoliv k nahlédnutí</div>
              <div className="text-gray-500 text-xs">Nastavení → Obchodní podmínky</div>
            </div>
          </div>
          <div className="flex gap-2">
            <LifeBuoy className="h-4 w-4 text-blue-600 shrink-0 mt-0.5"/>
            <div>
              <div className="font-medium text-gray-900">Podpora</div>
              <div className="text-gray-500 text-xs">
                {data.supportEmail || 'info@nyktech.cz'}
                {data.supportPhone ? <><br/>{data.supportPhone}</> : null}
              </div>
            </div>
          </div>
        </div>

        {/* Text podmínek */}
        <div className="px-7 pt-4 pb-2 flex items-baseline justify-between">
          <h3 className="font-semibold text-gray-900">{data.title}</h3>
          <span className="text-xs text-gray-400">
            verze {data.version}{data.effectiveDate ? ` · účinné od ${new Date(data.effectiveDate).toLocaleDateString('cs-CZ')}` : ''}
          </span>
        </div>
        <div ref={boxRef} onScroll={onScroll}
             className="mx-7 mb-4 flex-1 min-h-[220px] overflow-y-auto border border-gray-200 rounded-xl p-5 bg-gray-50">
          <div className="terms-body text-sm text-gray-700 leading-relaxed"
               dangerouslySetInnerHTML={{ __html: renderTermsHtml(data.body ?? '') }}/>
        </div>

        {/* Potvrzení */}
        <div className="px-7 py-4 border-t border-gray-100 bg-white space-y-3">
          {!scrolledEnd && (
            <p className="text-xs text-amber-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5"/> Přečtěte si prosím podmínky až do konce — poté půjde souhlas potvrdit.
            </p>
          )}
          <label className={`flex items-start gap-2.5 text-sm ${scrolledEnd ? 'text-gray-800' : 'text-gray-400'}`}>
            <input type="checkbox" disabled={!scrolledEnd} checked={checked}
                   onChange={e => setChecked(e.target.checked)}
                   className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
            <span>
              {data.consentText ||
                'Přečetl/a jsem si obchodní podmínky včetně zpracovatelské smlouvy a jako majitel účtu s nimi souhlasím. Beru na vědomí, že o souhlasu bude veden elektronický záznam (jméno, datum a čas, verze podmínek, IP adresa).'}
            </span>
          </label>

          {accept.isError && (
            <p className="text-sm text-red-600">
              Souhlas se nepodařilo uložit. Zkuste to prosím znovu, případně kontaktujte podporu.
            </p>
          )}

          <div className="flex justify-end">
            <button
              disabled={!checked || accept.isPending}
              onClick={() => accept.mutate(data.version)}
              className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
              {accept.isPending && <Loader2 className="h-4 w-4 animate-spin"/>}
              Souhlasím a pokračovat do systému
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
